import { useState, useEffect, useRef, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import TopNav from '../../components/TopNav'
import {
  getCustomer, getSchemas, getUploads, uploadFile, deleteUpload,
  getHydrationStatus, exportProductTablesReport
} from '../../api'
import LoadError from '../../components/LoadError'
import { loadErrorMessage, settledList } from '../../utils/loadState'
import { rebuildAnalytics } from '../../utils/hydration'
import './DataManagement.css'

function IconActual({ size = 16, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="1" y="3" width="14" height="3" rx="1" fill={color} opacity="0.9"/>
      <rect x="1" y="7.5" width="14" height="3" rx="1" fill={color} opacity="0.6"/>
      <rect x="1" y="12" width="14" height="2" rx="1" fill={color} opacity="0.3"/>
    </svg>
  )
}

function IconExpected({ size = 16, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="3" y="1" width="10" height="14" rx="1.5" stroke={color} strokeWidth="1.5"/>
      <line x1="5.5" y1="5" x2="10.5" y2="5" stroke={color} strokeWidth="1.2" strokeLinecap="round"/>
      <line x1="5.5" y1="8" x2="10.5" y2="8" stroke={color} strokeWidth="1.2" strokeLinecap="round"/>
      <line x1="5.5" y1="11" x2="8.5" y2="11" stroke={color} strokeWidth="1.2" strokeLinecap="round"/>
    </svg>
  )
}

function IconConfig({ size = 16, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="8" cy="8" r="2.2" stroke={color} strokeWidth="1.4"/>
      <path d="M8 1.5V3M8 13v1.5M1.5 8H3M13 8h1.5M3.4 3.4l1.1 1.1M11.5 11.5l1.1 1.1M3.4 12.6l1.1-1.1M11.5 4.5l1.1-1.1" stroke={color} strokeWidth="1.4" strokeLinecap="round"/>
    </svg>
  )
}

const TRUTH_OPTIONS = [
  {
    value: 'truth_1_actual',
    label: 'Truth 1 — Actual',
    desc: "What's real",
    example: 'Employees, pay runs, transactions',
    Icon: IconActual,
  },
  {
    value: 'truth_2_expected',
    label: 'Truth 2 — Expected',
    desc: "What's asked",
    example: 'Requirements, specs, BI report exports',
    Icon: IconExpected,
  },
  {
    value: 'truth_5_config',
    label: 'Truth 5 — Config',
    desc: 'Is config right',
    example: 'Pay groups, earning codes, system settings',
    Icon: IconConfig,
  },
]

export default function DataManagement() {
  const { customerId } = useParams()
  const fileInputRef = useRef(null)

  const [customer, setCustomer] = useState(null)
  const [tables, setTables] = useState([])
  const [tablesError, setTablesError] = useState(null)
  const [uploads, setUploads] = useState([])
  const [uploadsError, setUploadsError] = useState(null)
  const [selectedTable, setSelectedTable] = useState(null)
  const [hydrationStatus, setHydrationStatus] = useState(null)
  const [hydrating, setHydrating] = useState(false)
  // A failed status read or rebuild is shown with a Retry, never as "Current" (item 210).
  const [hydrationError, setHydrationError] = useState(null)
  const [hydrateError, setHydrateError] = useState(null)
  const [hydrateMsg, setHydrateMsg] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [loading, setLoading] = useState(true)
  const [dragOver, setDragOver] = useState(false)
  const [selectedTruth, setSelectedTruth] = useState('truth_1_actual')
  const [exportingReport, setExportingReport] = useState(false)

  const loadData = useCallback(async () => {
    try {
      const [ctx, sch, up, hyd] = await Promise.allSettled([
        getCustomer(customerId),
        getSchemas(customerId),
        getUploads(customerId),
        getHydrationStatus(customerId),
      ])
      if (ctx.status === 'fulfilled') setCustomer(ctx.value)
      // A failed table or upload read is said with a Retry, never kept as an empty or
      // stale list (item 213).
      const tl = settledList(sch, 'tables')
      setTables(tl.items || [])
      setTablesError(tl.error)
      const ul = settledList(up, 'uploads')
      setUploads(ul.items || [])
      setUploadsError(ul.error)
      setHydrationStatus(hyd.status === 'fulfilled' ? hyd.value : null)
      setHydrationError(hyd.status === 'fulfilled' ? null : loadErrorMessage(hyd.reason))
    } catch (err) {
      console.error('Load failed:', err)
    } finally {
      setLoading(false)
    }
  }, [customerId])

  useEffect(() => { loadData() }, [loadData])

  async function handleUpload(files) {
    if (!files || files.length === 0) return
    setUploading(true)
    try {
      for (const file of files) {
        await uploadFile(file, customerId, selectedTruth)
      }
      await loadData()
    } catch (err) {
      console.error('Upload failed:', err)
      alert('Upload failed: ' + err.message)
    } finally {
      setUploading(false)
    }
  }

  async function handleExportProductTables(productUid) {
    setExportingReport(true)
    try {
      const blob = await exportProductTablesReport(customerId, productUid)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${customerId}_${productUid}_tables.xlsx`
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Report export failed:', err)
      alert('Could not build the report: ' + err.message)
    } finally {
      setExportingReport(false)
    }
  }

  // Rebuild Analytics: start the rebuild, wait on ITS job, show what it did. A rebuild
  // that could not start or did not finish cleanly is shown with LoadError, never
  // swallowed (it used to be console.error only -- and polled forever on a failure).
  async function handleHydrate() {
    setHydrating(true)
    setHydrateError(null)
    setHydrateMsg(null)
    try {
      const { message } = await rebuildAnalytics(customerId, (s) => { setHydrationStatus(s); setHydrationError(null) })
      setHydrateMsg(message)
    } catch (err) {
      setHydrateError(loadErrorMessage(err))
    } finally {
      setHydrating(false)
      await loadData()
    }
  }

  async function handleDeleteUpload(uploadId) {
    if (!confirm('Delete this upload?')) return
    try {
      await deleteUpload(uploadId)
      await loadData()
    } catch (err) {
      console.error('Delete failed:', err)
    }
  }

  function onDrop(e) {
    e.preventDefault()
    setDragOver(false)
    handleUpload(e.dataTransfer.files)
  }

  const activeTruth = TRUTH_OPTIONS.find(t => t.value === selectedTruth) || TRUTH_OPTIONS[0]
  const companyName = customer?.company_name || customerId
  const totalRows = tables.reduce((sum, t) => sum + (t.row_count || 0), 0)

  const breadcrumbs = [
    { label: 'Customers', href: '/customers' },
    { label: companyName, href: `/customers/${customerId}` },
    { label: 'Data Management' },
  ]

  if (loading) {
    return (
      <>
        <TopNav breadcrumbs={breadcrumbs} actions={null} />
        <div className="dm-loading"><div className="dm-spinner" /><p>Loading data…</p></div>
      </>
    )
  }

  return (
    <>
      <TopNav breadcrumbs={breadcrumbs} actions={null} />

      <div className="dm-page">
        <div className="dm-grid">
          {/* Left: Upload + Uploads List */}
          <div className="dm-col">

            {/* Five Truths Classification */}
            <div className="dm-card dm-truth-card">
              <div className="dm-card-header">
                <h3 className="dm-card-title">Classify Upload</h3>
                <span className="dm-truth-active-label">{activeTruth.label}</span>
              </div>
              <p className="dm-truth-hint">Select the truth type before uploading.</p>
              <div className="dm-truth-options">
                {TRUTH_OPTIONS.map(t => {
                  const active = selectedTruth === t.value
                  return (
                    <button
                      key={t.value}
                      className={`dm-truth-option ${active ? 'selected' : ''}`}
                      onClick={() => setSelectedTruth(t.value)}
                    >
                      <span className="dm-truth-icon">
                        <t.Icon size={15} color={active ? '#020c30' : '#6b7280'} />
                      </span>
                      <div className="dm-truth-text">
                        <span className="dm-truth-label">{t.label}</span>
                        <span className="dm-truth-desc">{t.desc} · <em>{t.example}</em></span>
                      </div>
                      {active && <span className="dm-truth-check">✓</span>}
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Upload Zone */}
            <div
              className={`dm-upload-zone ${dragOver ? 'drag-over' : ''} ${uploading ? 'uploading' : ''}`}
              onDragOver={e => { e.preventDefault(); setDragOver(true) }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".csv,.xlsx,.xls,.tsv"
                style={{ display: 'none' }}
                onChange={e => handleUpload(e.target.files)}
              />
              {uploading ? (
                <>
                  <div className="dm-spinner" />
                  <p className="dm-upload-text">Uploading as <strong>{activeTruth.label}</strong>…</p>
                </>
              ) : (
                <>
                  <div className="dm-upload-icon">📁</div>
                  <p className="dm-upload-text">
                    Drop files here or <strong>click to browse</strong>
                  </p>
                  <p className="dm-upload-hint">
                    Classifying as <strong>{activeTruth.label}</strong> · CSV, Excel, TSV
                  </p>
                </>
              )}
            </div>

            {/* Hydration Controls */}
            <div className="dm-card">
              <div className="dm-card-header">
                <h3 className="dm-card-title">Hydration</h3>
                <span className={`dm-badge ${hydrationStatus?.needs_hydration ? 'pending' : 'ready'}`}>
                  {hydrating ? 'Running…' : hydrationError ? 'Unknown' : hydrationStatus?.needs_hydration ? 'Needs Rebuild' : 'Current'}
                </span>
              </div>
              {hydrationError && <LoadError inline what="hydration status" error={hydrationError} onRetry={loadData} />}
              <div className="dm-hydration-info">
                <p>{hydrationError ? '—' : (hydrationStatus?.duckdb_tables?.length || 0)} tables in analytics DB</p>
                {hydrationStatus?.supabase_uploads > 0 && (
                  <p className="dm-hydration-pending">{hydrationStatus.supabase_uploads} uploads pending hydration</p>
                )}
              </div>
              <button
                className="dm-hydrate-btn"
                onClick={handleHydrate}
                disabled={hydrating}
              >
                {hydrating ? 'Rebuilding Analytics…' : '🔄 Rebuild Analytics'}
              </button>
              {hydrateError && <LoadError inline what="the rebuilt tables" error={hydrateError} onRetry={handleHydrate} />}
              {hydrateMsg && !hydrateError && <p className="dm-hydration-done" role="status">{hydrateMsg}</p>}
            </div>

            {/* Uploads List */}
            <div className="dm-card">
              <div className="dm-card-header">
                <h3 className="dm-card-title">Uploads</h3>
                <span className="dm-card-count">{uploadsError ? '—' : uploads.length}</span>
              </div>
              {uploadsError ? (
                <LoadError what="uploads" error={uploadsError} onRetry={loadData} />
              ) : uploads.length === 0 ? (
                <p className="dm-empty">No files uploaded yet</p>
              ) : (
                <div className="dm-upload-list">
                  {uploads.map((u, i) => {
                    const uTruth = TRUTH_OPTIONS.find(t => t.value === u.truth_type)
                    return (
                      <div key={i} className="dm-upload-item">
                        <div className="dm-upload-file-icon">
                          {(u.file_name || '').endsWith('.csv') ? 'CSV' : 'XLS'}
                        </div>
                        <div className="dm-upload-info">
                          <div className="dm-upload-name">{u.file_name}</div>
                          <div className="dm-upload-meta">
                            {u.row_count ? `${u.row_count.toLocaleString()} rows` : ''} · {u.created_at?.split('T')[0] || ''}
                            {uTruth && <span className="dm-upload-truth"> · {uTruth.label}</span>}
                          </div>
                        </div>
                        <span className={`dm-upload-status ${u.status || 'ready'}`}>{u.status || 'ready'}</span>
                        <button className="dm-delete-btn" onClick={() => handleDeleteUpload(u.upload_id || u.id)}>×</button>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Right: Table Browser */}
          <div className="dm-col">
            <div className="dm-card dm-tables-card">
              <div className="dm-card-header">
                <span>
                  <h3 className="dm-card-title" style={{ display: 'inline' }}>Tables</h3>{' '}
                  <span className="dm-card-count">{tablesError ? "list couldn't load" : `${tables.length} tables · ${totalRows.toLocaleString()} rows`}</span>
                </span>
                <button
                  className="dm-export-report-btn"
                  disabled={exportingReport}
                  onClick={() => handleExportProductTables('ukg_pro_wfm')}
                  title="Download a spreadsheet of every WFM table populated for this customer, with row/column counts and real column lists"
                >
                  {exportingReport ? 'Building…' : 'Download WFM tables (.xlsx)'}
                </button>
              </div>
              {tablesError ? (
                <LoadError what="tables" error={tablesError} onRetry={loadData} />
              ) : tables.length === 0 ? (
                <p className="dm-empty">No tables loaded. Upload data and run hydration.</p>
              ) : (
                <div className="dm-table-browser">
                  <div className="dm-table-list">
                    {tables.map((t, i) => {
                      const name = (t.table_name || t.name || '').replace(`cust_${customerId}_api_`, '').replace(`cust_${customerId}_`, '')
                      const tTruth = TRUTH_OPTIONS.find(opt => opt.value === t.truth_type)
                      return (
                        <button
                          key={i}
                          className={`dm-table-row ${selectedTable === i ? 'selected' : ''}`}
                          onClick={() => setSelectedTable(selectedTable === i ? null : i)}
                        >
                          <span className="dm-table-icon">
                            {tTruth ? <tTruth.Icon size={13} color="#9ca3af" /> : '📋'}
                          </span>
                          <span className="dm-table-name">{name}</span>
                          <span className="dm-table-meta">{(t.row_count || 0).toLocaleString()} rows · {t.column_count || 0} cols</span>
                        </button>
                      )
                    })}
                  </div>

                  {selectedTable !== null && tables[selectedTable] && (() => {
                    const tbl = tables[selectedTable]
                    const tTruth = TRUTH_OPTIONS.find(opt => opt.value === tbl.truth_type)
                    return (
                      <div className="dm-column-detail">
                        <div className="dm-column-header">
                          <h4>{(tbl.table_name || tbl.name || '').replace(`cust_${customerId}_api_`, '').replace(`cust_${customerId}_`, '')}</h4>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {tTruth && (
                              <span className="dm-truth-active-label" style={{ fontSize: 10 }}>{tTruth.label}</span>
                            )}
                            <span className="dm-column-count">{tbl.column_count || 0} columns</span>
                          </div>
                        </div>
                        {tbl.columns ? (
                          <div className="dm-column-list">
                            {tbl.columns.map((col, j) => (
                              <div key={j} className="dm-column-item">
                                <span className="dm-col-name">{col.name}</span>
                                <span className="dm-col-type">{col.type}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="dm-empty">Column details not loaded with this schema endpoint</p>
                        )}
                        <div className="dm-table-dates">
                          {tbl.created_at && <span>Created: {tbl.created_at.split('T')[0]}</span>}
                          {tbl.source_file && <span>Source: {tbl.source_file}</span>}
                        </div>
                      </div>
                    )
                  })()}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
